import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/api';
import '../styles/AdminUsuarios.css';

const AdminUsuarios = () => {
  const { esAdmin } = useAuth();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('todos'); // todos, contratista, capataz
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    if (!esAdmin) {
      navigate('/');
      return;
    }

    cargarUsuarios();
  }, [esAdmin, navigate]);

  const [modalLicencia, setModalLicencia] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [codigoLicencia, setCodigoLicencia] = useState('');
  const [duracionDias, setDuracionDias] = useState('');
  const [procesando, setProcesando] = useState(false);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const response = await adminService.getUsuarios();
      setUsuarios(response.data);
      setError('');
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setError('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalLicencia = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setCodigoLicencia(usuario.codigo_licencia || '');
    setDuracionDias('365'); // Por defecto 1 año
    setModalLicencia(true);
  };

  const cerrarModalLicencia = () => {
    setModalLicencia(false);
    setUsuarioSeleccionado(null);
    setCodigoLicencia('');
    setDuracionDias('');
  };

  const handleActivarLicencia = async () => {
    if (!codigoLicencia.trim()) {
      alert('Por favor ingresa un código de licencia');
      return;
    }

    try {
      setProcesando(true);
      await adminService.activarLicencia({
        usuarioId: usuarioSeleccionado.id,
        codigoLicencia: codigoLicencia.trim(),
        duracionDias: duracionDias ? parseInt(duracionDias) : null
      });

      alert('Licencia activada exitosamente');
      cerrarModalLicencia();
      cargarUsuarios();
    } catch (err) {
      console.error('Error al activar licencia:', err);
      alert(err.response?.data?.error || 'Error al activar licencia');
    } finally {
      setProcesando(false);
    }
  };

  const handleDesactivarLicencia = async (usuario) => {
    if (!window.confirm(`¿Estás seguro de desactivar la licencia de ${usuario.nombre}?`)) {
      return;
    }

    try {
      setProcesando(true);
      await adminService.desactivarLicencia({ usuarioId: usuario.id });
      alert('Licencia desactivada exitosamente');
      cargarUsuarios();
    } catch (err) {
      console.error('Error al desactivar licencia:', err);
      alert(err.response?.data?.error || 'Error al desactivar licencia');
    } finally {
      setProcesando(false);
    }
  };

  const usuariosFiltrados = usuarios.filter(usuario => {
    // Filtrar por tipo
    if (filtro !== 'todos' && usuario.tipo !== filtro) {
      return false;
    }

    // Filtrar por búsqueda
    if (busqueda) {
      const searchLower = busqueda.toLowerCase();
      return (
        usuario.nombre?.toLowerCase().includes(searchLower) ||
        usuario.email?.toLowerCase().includes(searchLower) ||
        usuario.telefono?.includes(busqueda)
      );
    }

    return true;
  });

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getTipoBadgeClass = (tipo) => {
    switch (tipo) {
      case 'contratista':
        return 'badge-contratista';
      case 'capataz':
        return 'badge-capataz';
      default:
        return '';
    }
  };

  const getRolBadge = (rol) => {
    if (rol === 'admin') {
      return <span className="badge badge-admin">Admin</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="admin-usuarios">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-usuarios">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={cargarUsuarios} className="btn-retry">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-usuarios">
      <div className="page-header">
        <h1>Gestión de Usuarios</h1>
        <p className="subtitle">Lista completa de usuarios registrados en el sistema</p>
      </div>

      {/* Filtros y búsqueda */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar por nombre, email o teléfono..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${filtro === 'todos' ? 'active' : ''}`}
            onClick={() => setFiltro('todos')}
          >
            Todos ({usuarios.length})
          </button>
          <button
            className={`filter-btn ${filtro === 'contratista' ? 'active' : ''}`}
            onClick={() => setFiltro('contratista')}
          >
            Contratistas ({usuarios.filter(u => u.tipo === 'contratista').length})
          </button>
          <button
            className={`filter-btn ${filtro === 'capataz' ? 'active' : ''}`}
            onClick={() => setFiltro('capataz')}
          >
            Capataces ({usuarios.filter(u => u.tipo === 'capataz').length})
          </button>
        </div>

        <button onClick={cargarUsuarios} className="btn-refresh">
          Actualizar
        </button>
      </div>

      {/* Tabla de usuarios */}
      <div className="usuarios-table-container">
        <table className="usuarios-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Teléfono</th>
              <th>Tipo</th>
              <th>Licencia</th>
              <th>Proyectos</th>
              <th>Cotizaciones</th>
              <th>Fecha Registro</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuariosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">
                  {busqueda ? 'No se encontraron usuarios con esos criterios' : 'No hay usuarios para mostrar'}
                </td>
              </tr>
            ) : (
              usuariosFiltrados.map((usuario) => (
                <tr key={usuario.id}>
                  <td>{usuario.id}</td>
                  <td>
                    <div className="user-name">
                      {usuario.nombre}
                      {getRolBadge(usuario.rol)}
                    </div>
                  </td>
                  <td>{usuario.email}</td>
                  <td>{usuario.telefono || '-'}</td>
                  <td>
                    <span className={`badge ${getTipoBadgeClass(usuario.tipo)}`}>
                      {usuario.tipo === 'contratista' ? 'Contratista' : 'Capataz'}
                    </span>
                  </td>
                  <td className="text-center">
                    {usuario.licencia_activa ? (
                      <span className="badge badge-licencia-activa">✓ Activa</span>
                    ) : (
                      <span className="badge badge-licencia-inactiva">✗ Inactiva</span>
                    )}
                  </td>
                  <td className="text-center">{usuario.total_proyectos || 0}</td>
                  <td className="text-center">{usuario.total_cotizaciones || 0}</td>
                  <td>{formatearFecha(usuario.created_at)}</td>
                  <td className="text-center">
                    {usuario.licencia_activa ? (
                      <button
                        className="btn-desactivar-licencia"
                        onClick={() => handleDesactivarLicencia(usuario)}
                        disabled={procesando}
                      >
                        Desactivar
                      </button>
                    ) : (
                      <button
                        className="btn-activar-licencia"
                        onClick={() => abrirModalLicencia(usuario)}
                        disabled={procesando}
                      >
                        Activar
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Resumen */}
      <div className="summary-section">
        <div className="summary-card">
          <span className="summary-label">Total usuarios mostrados:</span>
          <span className="summary-value">{usuariosFiltrados.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total contratistas:</span>
          <span className="summary-value">
            {usuariosFiltrados.filter(u => u.tipo === 'contratista').length}
          </span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total capataces:</span>
          <span className="summary-value">
            {usuariosFiltrados.filter(u => u.tipo === 'capataz').length}
          </span>
        </div>
      </div>

      {/* Modal para activar licencia */}
      {modalLicencia && (
        <div className="modal-overlay" onClick={cerrarModalLicencia}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Activar Licencia</h2>
              <button className="btn-close-modal" onClick={cerrarModalLicencia}>✕</button>
            </div>

            <div className="modal-body">
              <p><strong>Usuario:</strong> {usuarioSeleccionado?.nombre}</p>
              <p><strong>Email:</strong> {usuarioSeleccionado?.email}</p>
              <p><strong>Tipo:</strong> {usuarioSeleccionado?.tipo}</p>

              <div className="form-group">
                <label>Código de Licencia *</label>
                <input
                  type="text"
                  value={codigoLicencia}
                  onChange={(e) => setCodigoLicencia(e.target.value)}
                  placeholder="Ingresa el código de licencia"
                  className="input-licencia"
                />
              </div>

              <div className="form-group">
                <label>Duración (días)</label>
                <input
                  type="number"
                  value={duracionDias}
                  onChange={(e) => setDuracionDias(e.target.value)}
                  placeholder="365 (opcional, vacío = ilimitado)"
                  className="input-licencia"
                />
                <small>Dejar vacío para licencia sin vencimiento</small>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={cerrarModalLicencia}
                disabled={procesando}
              >
                Cancelar
              </button>
              <button
                className="btn-confirm"
                onClick={handleActivarLicencia}
                disabled={procesando}
              >
                {procesando ? 'Activando...' : 'Activar Licencia'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsuarios;
